import React, {useEffect, useMemo, useState} from 'react';
import {useLazyQuery} from '@apollo/client';
import {Box, Chip, Tooltip} from '@mui/material';
import {grey} from '@mui/material/colors';

import ChildDetailsTooltip from '../sessions/ChildDetailsTooltip';
import InternalTagsDialog from './InternalTagsDialog';
import MemberJoinStatus from '../LiveDashboard/MemberJoinStatus';
import {ApolloFetchPolicies} from '../../constants/FetchPolicies';
import {toTitleCase} from '../../helpers/TextUtils';
import {FETCH_CHILD_TAG_METRICS} from '../../queries/GraphQLQueries';
import ChildDetailTooltipLabel from "./ChildDetailTooltipLabel";

const styles = {
    box: {
        childRow: {
            display: 'flex',
            alignItems: 'center',
            my: '-0.2rem',
        },
    },
    chip: {
        sessionChild: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.875rem',
            '& .MuiChip-label': {
                overflow: 'visible',
                textOverflow: 'clip',
                whiteSpace: 'normal',
                width: 'auto',
            },
        },
    },
    tooltip: {
        bgcolor: grey[200],
        color: 'black',
        '& .MuiTooltip-arrow': {
            color: grey[200],
        },
    }
};

const MemberIdChipWithTags = ({child, statusColor, statusMessage, showMemberJoiningTime, memberJoiningTime, memberId, session, bookingId, attendanceReport, onChildDetailsDrilledInto}) => {
    const [isTagsDialogOpen, setIsTagsDialogOpen] = useState(false);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    const [fetchChildTagMetrics, {data}] = useLazyQuery(FETCH_CHILD_TAG_METRICS, {
        fetchPolicy: ApolloFetchPolicies.NETWORK_ONLY
    });

    useEffect(() => {
        if (child?.id) {
            fetchChildTagMetrics({
                variables: {childId: child.id}
            });
        }
    }, [child.id, fetchChildTagMetrics]);

    const tagMetrics = useMemo(() => {
        if (!data?.fetchChildTagMetrics) {
            return null;
        }

        return data.fetchChildTagMetrics.filter(tagMetric => tagMetric.tag.isInternal);
    }, [data?.fetchChildTagMetrics]);

    const handleOpenTagsDialog = () => {
        setIsTagsDialogOpen(true);
        setIsTooltipOpen(false);
    };

    const handleCloseTagsDialog = () => {
        setIsTagsDialogOpen(false);
        fetchChildTagMetrics({
            variables: {childId: child.id}
        });
    };

    return (
        <>
            <Box sx={styles.box.childRow}>
                <MemberJoinStatus
                    message={statusMessage}
                    color={statusColor}
                    displayTime={showMemberJoiningTime}
                    joiningTime={memberJoiningTime}
                />
                <Tooltip
                    open={isTooltipOpen}
                    onClose={() => setIsTooltipOpen(false)}
                    onMouseEnter={() => setIsTooltipOpen(true)}
                    disableFocusListener
                    placement="right"
                    arrow
                    slotProps={{tooltip: {sx: attendanceReport && styles.tooltip}}}
                    title={attendanceReport
                        ? <ChildDetailsTooltip
                            child={child}
                            serviceType={session?.serviceType?.keycode}
                            attendanceReport={attendanceReport}
                            tagMetrics={tagMetrics}
                            onOpenTagsDialog={handleOpenTagsDialog}/>
                        : <ChildDetailTooltipLabel
                            child={child}
                            onChildDetailsDrilledInto={onChildDetailsDrilledInto}
                            serviceType={session?.serviceType?.keycode}
                            attendanceReport={attendanceReport}
                            showHistory={false}/>
                        }
                >
                    <Chip size="small" label={toTitleCase(memberId)} sx={styles.chip.sessionChild} />
                </Tooltip>
            </Box>

            <InternalTagsDialog
                isTagsDialogOpen={isTagsDialogOpen}
                handleCloseTagsDialog={handleCloseTagsDialog}
                child={child}
                tagMetrics={tagMetrics}
                bookingId={bookingId}
                sessionStartTime={session.from}
                onTagsUpdated={() => fetchChildTagMetrics({variables: {childId: child.id}})}
            />
        </>
    );
};

export default MemberIdChipWithTags;